import { supabase } from '../lib/supabase';
import type {
  CoachingConversation,
  CoachingMessage,
  SendMessageRequest,
  SendMessageResponse,
  StartSessionRequest,
  EndSessionRequest,
  CoachingGoal,
  MessageRole,
  UrgencyLevel,
  UserMood
} from '../types/coaching-chat.types';
import type { UserCareerContext } from '../types/ai-coach-context.types';

export class CoachingChatService {
  private static async checkQuota(userId: string): Promise<{
    canSend: boolean;
    quota: any;
  }> {
    const { data, error } = await supabase.rpc('check_coaching_quota', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error checking quota:', error);
      return { canSend: false, quota: null };
    }

    return {
      canSend: data.has_message_quota && data.has_token_quota,
      quota: data
    };
  }

  static async getUserQuota(userId: string) {
    const { data, error } = await supabase.rpc('check_coaching_quota', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error fetching quota:', error);
      throw new Error('Failed to fetch usage quota');
    }

    return data;
  }

  static async startConversation(
    userId: string,
    request: StartSessionRequest
  ): Promise<CoachingConversation> {
    const quotaCheck = await this.checkQuota(userId);
    if (!quotaCheck.canSend) {
      throw new Error('Monthly message limit reached. Upgrade to continue.');
    }

    const { data, error } = await supabase
      .from('coaching_conversations')
      .insert({
        user_id: userId,
        coaching_goal: request.coachingGoal || 'general',
        session_started_at: new Date().toISOString(),
        status: 'active',
        total_messages: 0
      })
      .select()
      .single();

    if (error) throw error;

    await supabase.rpc('increment_coaching_usage', {
      p_user_id: userId,
      p_messages: 0,
      p_tokens: 0,
      p_voice_minutes: 0,
      p_new_conversation: true
    });

    return data;
  }

  static async sendMessage(
    userId: string,
    request: SendMessageRequest,
    userContext: UserCareerContext
  ): Promise<SendMessageResponse> {
    const quotaCheck = await this.checkQuota(userId);
    if (!quotaCheck.canSend) {
      throw new Error('Monthly message limit reached. Please upgrade your plan or wait for the next billing cycle.');
    }

    let conversationId = request.conversationId;

    if (!conversationId) {
      const conversation = await this.startConversation(userId, {
        coachingGoal: request.coachingGoal,
        initialMessage: request.content
      });
      conversationId = conversation.id;
    }

    const conversation = await this.getConversation(conversationId);
    if (!conversation) {
      throw new Error('Conversation not found');
    }

    const userMessage: Partial<CoachingMessage> = {
      conversation_id: conversationId,
      role: 'user',
      content: request.content,
      urgency_level: request.urgencyLevel || 'medium',
      user_mood: request.userMood || null,
      context_snapshot: userContext as any,
      tokens_used: 0,
      metadata: request.attachments ? { attachments: request.attachments } : {}
    };

    const { data: savedUserMessage, error: userError } = await supabase
      .from('coaching_messages')
      .insert(userMessage)
      .select()
      .single();

    if (userError) throw userError;

    const conversationHistory = await this.getConversationMessages(conversationId);

    const aiResponse = await this.callAICoachAPI(
      conversationHistory,
      userContext,
      conversation.coaching_goal || 'general',
      request.userMood || 'neutral',
      request.urgencyLevel || 'medium'
    );

    const assistantMessage: Partial<CoachingMessage> = {
      conversation_id: conversationId,
      role: 'assistant',
      content: aiResponse.message,
      urgency_level: aiResponse.urgencyLevel || 'medium',
      user_mood: null,
      context_snapshot: {},
      tokens_used: aiResponse.tokensUsed || 0,
      metadata: {
        actionButtons: aiResponse.actionButtons,
        coachingExercise: aiResponse.coachingExercise,
        followUpSuggestions: aiResponse.followUpSuggestions,
        resourceRecommendations: aiResponse.resourceRecommendations,
        quickReplies: aiResponse.quickReplies
      }
    };

    const { data: savedAssistantMessage, error: assistantError } = await supabase
      .from('coaching_messages')
      .insert(assistantMessage)
      .select()
      .single();

    if (assistantError) throw assistantError;

    await supabase.rpc('increment_coaching_usage', {
      p_user_id: userId,
      p_messages: 1,
      p_tokens: aiResponse.tokensUsed || 0,
      p_voice_minutes: 0,
      p_new_conversation: false
    });

    const updatedConversation = await this.getConversation(conversationId);

    return {
      conversationId,
      userMessage: savedUserMessage as CoachingMessage,
      assistantMessage: savedAssistantMessage as CoachingMessage,
      conversation: updatedConversation!
    };
  }

  private static async callAICoachAPI(
    conversationHistory: CoachingMessage[],
    userContext: UserCareerContext,
    coachingGoal: CoachingGoal,
    userMood: UserMood,
    urgencyLevel: UrgencyLevel
  ): Promise<{
    message: string;
    tokensUsed: number;
    actionButtons?: any[];
    coachingExercise?: any;
    followUpSuggestions?: string[];
    resourceRecommendations?: any[];
    quickReplies?: string[];
    urgencyLevel?: UrgencyLevel;
  }> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-coach-chat`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conversationHistory: conversationHistory.slice(-10),
        userContext,
        coachingGoal,
        userMood,
        urgencyLevel
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get AI response');
    }

    return await response.json();
  }

  static async getConversation(conversationId: string): Promise<CoachingConversation | null> {
    const { data, error } = await supabase
      .from('coaching_conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (error) return null;
    return data;
  }

  static async getConversationMessages(conversationId: string): Promise<CoachingMessage[]> {
    const { data, error } = await supabase
      .from('coaching_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) return [];
    return data;
  }

  static async getUserConversations(userId: string): Promise<CoachingConversation[]> {
    const { data, error } = await supabase
      .from('coaching_conversations')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) return [];
    return data;
  }

  static async endConversation(request: EndSessionRequest): Promise<void> {
    await supabase
      .from('coaching_conversations')
      .update({
        status: 'completed',
        session_ended_at: new Date().toISOString()
      })
      .eq('id', request.conversationId);

    if (request.effectivenessRating || request.userFeedback || request.actionItems) {
      const { data: conversation } = await supabase
        .from('coaching_conversations')
        .select('user_id')
        .eq('id', request.conversationId)
        .single();

      if (conversation) {
        await supabase.from('coaching_sessions').insert({
          user_id: conversation.user_id,
          conversation_id: request.conversationId,
          session_type: 'on_demand',
          effectiveness_rating: request.effectivenessRating,
          user_feedback: request.userFeedback,
          action_items: request.actionItems || [],
          ended_at: new Date().toISOString()
        });
      }
    }
  }

  static async pauseConversation(conversationId: string): Promise<void> {
    await supabase
      .from('coaching_conversations')
      .update({ status: 'paused' })
      .eq('id', conversationId);
  }

  static async resumeConversation(conversationId: string): Promise<void> {
    await supabase
      .from('coaching_conversations')
      .update({ status: 'active' })
      .eq('id', conversationId);
  }

  static async deleteConversation(conversationId: string): Promise<void> {
    await supabase
      .from('coaching_conversations')
      .delete()
      .eq('id', conversationId);
  }

  static async transcribeAudio(audioBlob: Blob): Promise<string> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-audio`;

    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || 'Failed to transcribe audio';

      if (response.status === 503) {
        throw new Error('Voice transcription is not configured. Please ensure OPENAI_API_KEY is set in Supabase environment.');
      } else if (response.status === 429) {
        throw new Error('OpenAI rate limit reached. Please wait a moment and try again.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.text;
  }

  static async synthesizeSpeech(
    text: string,
    voice: string = 'alloy',
    speed: number = 1.0,
    instruction?: string
  ): Promise<Blob> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/synthesize-speech`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        text,
        voice,
        speed,
        instruction
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to synthesize speech');
    }

    return await response.blob();
  }

  static async trackVoiceUsage(userId: string, audioSeconds: number): Promise<void> {
    const minutes = Math.ceil(audioSeconds / 60);

    if (minutes > 0) {
      await supabase.rpc('increment_coaching_usage', {
        p_user_id: userId,
        p_messages: 0,
        p_tokens: 0,
        p_voice_minutes: minutes,
        p_new_conversation: false
      });
    }
  }
}
